import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";

export default function Validation() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8">
      <SlideHeader eyebrow="Validation">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-6xl md:leading-[1.1]">
          Live in production. Paying customers. Pipeline closing.
        </h1>
        <p className="mt-3 text-base italic leading-snug text-slate-600 md:mt-4 md:text-xl">
          More organic installs. Fewer hours. Proven on your data — these are
          the numbers behind the claim.
        </p>
      </SlideHeader>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
        <MetricTile
          value="105"
          label="Active accounts"
          subtitle="across the platform"
        />
        <MetricTile
          value="269"
          label="Projects under management"
          subtitle="customer apps tracked"
        />
        <MetricTile
          value="~13,000"
          label="ASO audits run end-to-end"
          subtitle="in production, to date"
        />
      </div>
      <div className="mt-5 grid grid-cols-1 gap-6 md:mt-6 md:flex-1 md:grid-cols-2 md:gap-10">
        <div className="space-y-5">
          <Section label="Design Partners">
            <p className="text-base leading-relaxed text-slate-700">
              <span className="font-semibold text-slate-900">~30</span> app
              publishers, ASO professionals, agency operators across gaming,
              health & fitness, lifestyle, fintech. Active feedback loop;
              product roadmap shaped by their use.
            </p>
          </Section>
          <Section label="Beta Users">
            <p className="text-base leading-relaxed text-slate-700">
              <span className="font-semibold text-slate-900">~70</span>{" "}
              self-serve accounts on Free + Basic tiers running audits on
              their own apps.
            </p>
          </Section>
          <Section label="Evangelists & Affiliates">
            <ul className="space-y-1.5 text-base leading-snug text-slate-700">
              <Bullet>
                <span className="font-semibold text-slate-900">
                  Two senior ASO practitioners
                </span>{" "}
                actively evangelizing Apptonomy in their networks.
              </Bullet>
              <Bullet>
                <span className="font-semibold text-slate-900">
                  10 affiliates
                </span>{" "}
                approved on Rewardful.
              </Bullet>
            </ul>
          </Section>
        </div>

        <div className="space-y-5">
          <Section label="Commercial signal">
            <ul className="space-y-2 text-base leading-snug text-slate-700">
              <Bullet>
                <span className="font-semibold text-slate-900">Pickem</span> —
                paying customer since May 2026.
              </Bullet>
              <Bullet>
                <span className="font-semibold text-slate-900">Geeklab</span>{" "}
                — partnership agreed; logos, case studies, referral
                cross-marketing.
              </Bullet>
              <Bullet>
                <span className="font-semibold text-slate-900">
                  Terminal 4
                </span>{" "}
                — logo confirmed by CEO.
              </Bullet>
            </ul>
          </Section>
          <Section label="Pipeline">
            <ul className="space-y-2 text-base leading-snug text-slate-700">
              <Bullet>
                <span className="font-semibold text-slate-900">
                  14 priority opportunities
                </span>{" "}
                in active BD conversations across gaming publishers, ASO
                agencies, and enterprise consumer brands.
              </Bullet>
              <Bullet>
                <span className="font-semibold text-slate-900">
                  LOI in legal review
                </span>{" "}
                with a major consumer-software brand.
              </Bullet>
              <Bullet>
                <span className="font-semibold text-slate-900">
                  Enterprise trials active
                </span>{" "}
                with notable mobile-game publishers and a major consumer-app
                publisher under NDA.
              </Bullet>
              <Bullet>
                <span className="font-semibold text-slate-900">
                  3 locked logos
                </span>{" "}
                +{" "}
                <span className="font-semibold text-slate-900">
                  3 high-probability deals
                </span>{" "}
                in active negotiation.
              </Bullet>
            </ul>
          </Section>
        </div>
      </div>

      <div className="mt-4 rounded-lg bg-blue-50 p-3 ring-1 ring-blue-200 md:p-4">
        <p className="text-sm leading-snug text-slate-800 md:text-base">
          <span className="font-semibold text-slate-900">
            Attribution discipline:
          </span>{" "}
          we promise{" "}
          <span className="font-semibold text-slate-900">
            organic install growth
          </span>
          , prove conversion + visibility deltas on the customer's own store
          data, and concede causal install attribution to a paid pilot or
          geo-holdout — never claimed from the dashboard.
        </p>
      </div>
    </div>
  );
}

function MetricTile({
  value,
  label,
  subtitle,
}: {
  value: string;
  label: string;
  subtitle: string;
}) {
  return (
    <div className="rounded-lg bg-slate-50 p-4 ring-1 ring-slate-200 md:p-5">
      <p className="text-3xl font-bold leading-none text-blue-700 md:text-4xl">
        {value}
      </p>
      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-900 md:text-sm">
        {label}
      </p>
      <p className="mt-1 text-xs text-slate-600 md:text-sm">{subtitle}</p>
    </div>
  );
}

function Section({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-700">
        {label}
      </p>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Bullet({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-2">
      <span className="text-blue-600">•</span>
      <span>{children}</span>
    </li>
  );
}
